import React, { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './QuizDatePicker.module.scss';
import cx from 'classnames';
import ArrowIcon from '../../../assets/icons/arrowBtn.svg';
import CalendarIcon from '../../../assets/icons/calendar.svg';

// Register German locale
registerLocale('de', de);

interface QuizDatePickerProps {
  value?: string;
  onSubmit: (value: string) => void;
  onSkip?: () => void;
  showSkip?: boolean;
  placeholder?: string;
}

const QuizDatePicker: React.FC<QuizDatePickerProps> = ({
  value: initialValue = '',
  onSubmit,
  onSkip,
  showSkip = true,
  placeholder = 'TT.MM.JJJJ',
}) => {
  const [dateValue, setDateValue] = useState<Date | null>(
    initialValue ? parseDate(initialValue) : null
  );
  const [inputValue, setInputValue] = useState(initialValue);

  // Parse date from DD.MM.YYYY format
  function parseDate(str: string): Date | null {
    const parts = str.split('.');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    return null;
  }

  // Handle date selection from calendar
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formatted = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
      setDateValue(date);
      setInputValue(formatted);
    }
  };

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onSubmit(inputValue);
    } else if (onSkip) {
      onSkip();
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onSubmit('-');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className={styles.quizDatePicker}>
      <div className={styles.inputWrapper}>
        <div className={styles.inputContainer}>
          <DatePicker
            selected={dateValue}
            onChange={handleDateChange}
            dateFormat="dd.MM.yyyy"
            locale="de"
            maxDate={new Date()}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
            placeholderText={placeholder}
            className={cx(styles.dateInput, { [styles.hasValue]: inputValue })}
            calendarClassName={styles.datepickerCalendar}
            onKeyDown={handleKeyDown}
            openToDate={dateValue || new Date(1965, new Date().getMonth(), new Date().getDate())}
            showIcon
            icon={<img src={CalendarIcon} alt="Calendar" className={styles.calendarIcon} />}
            toggleCalendarOnIconClick
          />
        </div>
        <button className={styles.submitBtn} onClick={handleSubmit} type="button">
          <img src={ArrowIcon} alt="Submit" />
        </button>
      </div>

      {showSkip && (
        <button className={styles.skipBtn} onClick={handleSkip} type="button">
          Überspringen
        </button>
      )}
    </div>
  );
};

export default QuizDatePicker;
